import { getUserPhoto } from './graphService';
import { FUNCTION_KEY } from './backendPhotoService';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

/**
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] Stops the queue draining. In-flight
 *   uploads finish — aborting mid-PUT would leave a partial blob in storage —
 *   but nothing new is started. Without this a 739-photo run could not be
 *   stopped at all: navigating away in an SPA does not unwind the loop, so the
 *   only exit was closing the tab.
 */
export async function syncPhotosToBackend(employees, token, onProgress, { signal } = {}) {
  if (!API_BASE) return { synced: 0, skipped: 0, total: employees.length };

  // Find which employees are already cached
  let cachedIds = new Set();
  try {
    const res = await fetch(`${API_BASE}/photos/manifest`);
    if (res.ok) cachedIds = new Set(Object.keys(await res.json()));
  } catch { /* manifest is best-effort; fall back to syncing everyone */ }

  const toSync = employees.filter((e) => !cachedIds.has(e.id));
  if (toSync.length === 0) {
    return { synced: 0, skipped: cachedIds.size, total: employees.length };
  }

  const CONCURRENCY = 10;
  const queue = [...toSync];
  let active = 0;
  let done = 0;
  let synced = 0;

  await new Promise((resolve) => {
    const next = () => {
      if (signal?.aborted) {
        // Resolve once the last in-flight upload lands, not immediately —
        // otherwise the caller reports a total that is still climbing.
        if (active === 0) resolve();
        return;
      }
      while (active < CONCURRENCY && queue.length > 0) {
        const employee = queue.shift();
        active++;
        getUserPhoto(token, employee.id)
          .then(async (blob) => {
            if (blob) {
              try {
                const headers = { 'Content-Type': blob.type || 'image/jpeg' };
                if (FUNCTION_KEY) headers['x-functions-key'] = FUNCTION_KEY;
                await fetch(`${API_BASE}/photos/${employee.id}`, {
                  method: 'PUT',
                  headers,
                  body: blob,
                });
                synced++;
              } catch { /* skip employees whose photo upload fails */ }
            }
          })
          .catch(() => {})
          .finally(() => {
            active--;
            done++;
            onProgress?.(done, toSync.length, cachedIds.size);
            next();
          });
      }
      // Nothing running and nothing queued — either finished or stopped.
      if (active === 0 && queue.length === 0) resolve();
    };
    next();
  });

  return {
    synced,
    skipped: cachedIds.size,
    total: employees.length,
    attempted: done,
    remaining: toSync.length - done,
    stopped: signal?.aborted === true,
  };
}
